import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Clock } from "lucide-react";
import { TYPE_STYLES, formatHubDate, isHubBookmarked, toggleHubBookmark, trackHubEvent } from "@/utils/hub";

export function HubCard({ item, compact = false }) {
  const [saved, setSaved] = useState(() => isHubBookmarked(item.id));
  const t = TYPE_STYLES[item.type] || TYPE_STYLES.article;
  const watch = item.type === "youtube" || item.type === "video";

  useEffect(() => {
    const h = () => setSaved(isHubBookmarked(item.id));
    window.addEventListener("hub-bookmarks-changed", h);
    return () => window.removeEventListener("hub-bookmarks-changed", h);
  }, [item.id]);

  const onBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const nowSaved = toggleHubBookmark(item.id);
    setSaved(nowSaved);
    trackHubEvent("bookmark", item.id, { saved: nowSaved });
  };

  return (
    <Link
      to={`/wellness-hub/${item.slug}`}
      className="group bg-white border-2 border-navy/10 overflow-hidden hover:border-navy/45 hover:-translate-y-0.5 transition-[border-color,transform] duration-200 flex flex-col"
      data-testid={`hub-card-${item.slug}`}
    >
      <div className="relative aspect-video bg-slate-100 overflow-hidden">
        {item.coverImageUrl && (
          <img
            src={item.coverImageUrl}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        )}
        <span className={`absolute top-2.5 left-2.5 text-[10px] font-mono font-bold uppercase px-2.5 py-1 ${t.cls}`}>
          {t.label}
        </span>
        {item.isSponsored && (
          <span className="absolute top-2.5 left-24 text-[10px] font-mono font-bold uppercase px-2.5 py-1 bg-amber-100 text-amber-800" data-testid={`sponsored-badge-${item.slug}`}>
            Sponsored
          </span>
        )}
        <button
          onClick={onBookmark}
          aria-label={saved ? "Remove bookmark" : "Save for later"}
          className="absolute top-2 right-2 p-2 bg-white/90 hover:bg-white shadow transition-colors"
          data-testid={`hub-bookmark-${item.slug}`}
        >
          <Bookmark className={`w-4 h-4 ${saved ? "fill-brand-red text-brand-red" : "text-gray-500"}`} />
        </button>
      </div>
      <div className={`flex flex-col flex-1 ${compact ? "p-4" : "p-5"}`}>
        {item.topic && <span className="font-mono text-[10px] font-bold uppercase text-brand-blue mb-1.5">{item.topic}</span>}
        <h3 className={`font-heading font-bold text-navy leading-snug group-hover:text-brand-red transition-colors ${compact ? "text-base" : "text-lg"}`}>
          {item.title}
        </h3>
        {!compact && item.excerpt && (
          <p className="font-body text-sm text-gray-500 mt-2 line-clamp-2">{item.excerpt}</p>
        )}
        <div className="flex items-center gap-2 font-body text-xs text-gray-500 mt-auto pt-3">
          <span className="truncate">{item.author}</span>
          <span>·</span>
          <span>{formatHubDate(item.publishDate)}</span>
          <span className="ml-auto flex items-center gap-1 whitespace-nowrap">
            <Clock className="w-3 h-3" /> {item.minutes} min {watch ? "watch" : "read"}
          </span>
        </div>
      </div>
    </Link>
  );
}
